import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronRight, Download, ExternalLink, FileInput, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DocumentTypeLabel, StatusBadge } from '@/components/app/status-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NlDateInput } from '@/components/ui/nl-date-input';
import { Select } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { api, documentPdfUrl, type DocumentDetail } from '@/lib/api';
import {
	AUDIT_EVENT_LABELS,
	DIRECTIONS,
	DOCUMENT_TYPES,
	PAYMENT_STATUS_DOT,
	PAYMENT_STATUS_LABELS,
	REFERENCE_TYPES,
} from '@/lib/labels';
import { deleteDocument, patchDocument, promoteDocumentToInkoop, retryDocument } from '@/lib/mutations';
import { formatAmount, formatDateISO, formatDateTimeISO } from '@/lib/utils';

export const Route = createFileRoute('/documents/$id')({
	component: DocumentDetailView,
});

function DocumentDetailView() {
	const { id } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [editing, setEditing] = useState(false);
	const [auditOpen, setAuditOpen] = useState(false);

	const query = useQuery({
		queryKey: ['documents', 'detail', id],
		queryFn: () => api.document(id),
	});

	const retryMutation = useMutation({
		mutationFn: () => retryDocument(id),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['documents'] });
		},
	});

	// Archief-document naar /inkoopfacturen verplaatsen (bv. een kassabon die de
	// classifier als 'receipt' zag en daarom uit de inkoop-werklijst viel).
	const toInkoopMutation = useMutation({
		mutationFn: () => promoteDocumentToInkoop(id),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['documents'] });
			await queryClient.invalidateQueries({ queryKey: ['inkoopfacturen'] });
			navigate({ to: '/inkoopfacturen/$id', params: { id } });
		},
	});

	const deleteMutation = useMutation({
		mutationFn: () => deleteDocument(id),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['documents'] });
			navigate({ to: '/documents' });
		},
	});

	const confirmDelete = () => {
		const ok = window.confirm(
			'Weet je zeker dat je dit document wilt verwijderen?\n\nHet PDF-bestand, alle metadata en de audit-trail van dit document worden definitief verwijderd. Deze actie is onomkeerbaar.',
		);
		if (ok) deleteMutation.mutate();
	};

	if (query.isLoading) {
		return (
			<div className="mx-auto flex max-w-md flex-col items-center gap-3 p-16 text-sm text-muted-foreground">
				<img src="/docr-logo-400.png" alt="DocR" className="h-7 w-auto select-none" draggable={false} />
				<span>Document laden…</span>
			</div>
		);
	}
	if (query.isError || !query.data) {
		const status = query.error && typeof query.error === 'object' && 'status' in query.error
			? (query.error as { status: number }).status
			: null;
		const isNotFound = status === 404;
		const isForbidden = status === 403;
		return (
			<div className="mx-auto max-w-md p-12 text-center">
				<img src="/docr-logo-400.png" alt="DocR" className="mx-auto mb-4 h-7 w-auto select-none" draggable={false} />
				<div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
					<AlertTriangle className="h-5 w-5" />
				</div>
				<h2 className="mt-3 text-base font-semibold">
					{isNotFound ? 'Document niet gevonden' : isForbidden ? 'Geen toegang' : 'Kon document niet laden'}
				</h2>
				<p className="mt-1 text-sm text-muted-foreground">
					{isNotFound
						? 'Dit document bestaat niet (meer) of is verwijderd.'
						: isForbidden
						? 'Dit document hoort bij een entiteit waar jij geen toegang toe hebt.'
						: 'Er ging iets mis bij het ophalen. Probeer het opnieuw.'}
				</p>
				<div className="mt-4 flex justify-center gap-2">
					<Button variant="outline" size="sm" onClick={() => navigate({ to: '/documents' })}>
						<ArrowLeft className="mr-1.5 h-4 w-4" />
						Terug naar overzicht
					</Button>
					{!isNotFound && !isForbidden && (
						<Button size="sm" onClick={() => query.refetch()}>
							Opnieuw proberen
						</Button>
					)}
				</div>
			</div>
		);
	}

	const doc = query.data.document;
	const audit = query.data.audit_trail;

	return (
		<div className="mx-auto max-w-screen-2xl space-y-6">
			<div className="flex flex-wrap items-start gap-3">
				<Button variant="ghost" size="sm" asChild>
					<Link to="/documents">
						<ArrowLeft className="mr-1.5 h-4 w-4" />
						Terug
					</Link>
				</Button>
				<div className="flex-1">
					<h1 className="truncate text-xl font-semibold">
						{doc.counterparty_name ?? doc.original_filename ?? 'Document'}
					</h1>
					<p className="mt-0.5 text-xs text-muted-foreground">
						{doc.entity_name} · {doc.original_filename} · {Math.round(doc.file_size_bytes / 1024)} KB
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<StatusBadge status={doc.status} />
					{doc.needs_review ? <Badge variant="warning">review nodig</Badge> : null}
					<Button size="sm" variant="outline" asChild>
						<a href={documentPdfUrl(id)} target="_blank" rel="noreferrer">
							<ExternalLink className="mr-1.5 h-4 w-4" />
							Open PDF
						</a>
					</Button>
					<Button size="sm" variant="outline" asChild>
						<a href={documentPdfUrl(id)} download={doc.original_filename ?? 'document.pdf'}>
							<Download className="mr-1.5 h-4 w-4" />
							Download
						</a>
					</Button>
					<Button
						size="sm"
						variant="outline"
						disabled={retryMutation.isPending || ['duplicate', 'skipped'].includes(doc.status)}
						onClick={() => retryMutation.mutate()}
					>
						<RefreshCw className={retryMutation.isPending ? 'mr-1.5 h-4 w-4 animate-spin' : 'mr-1.5 h-4 w-4'} />
						Opnieuw verwerken
					</Button>
					{doc.target !== 'exact' && doc.status === 'completed' && (
						<Button
							size="sm"
							variant="outline"
							disabled={toInkoopMutation.isPending}
							title="Verplaats dit document naar de inkoopfacturen-werklijst zodat het in Exact geboekt kan worden"
							onClick={() => toInkoopMutation.mutate()}
						>
							<FileInput className="mr-1.5 h-4 w-4" />
							Naar inkoopfacturen
						</Button>
					)}
					{!editing && (
						<Button size="sm" onClick={() => setEditing(true)}>
							<Pencil className="mr-1.5 h-4 w-4" />
							Bewerken
						</Button>
					)}
					<Button
						size="sm"
						variant="outline"
						className="text-destructive hover:bg-destructive/10 hover:text-destructive"
						disabled={deleteMutation.isPending}
						onClick={confirmDelete}
					>
						<Trash2 className={deleteMutation.isPending ? 'mr-1.5 h-4 w-4 animate-pulse' : 'mr-1.5 h-4 w-4'} />
						Verwijderen
					</Button>
				</div>
			</div>

			{doc.status === 'error' && doc.status_reason && (
				<Card className="border-destructive/40 bg-destructive/5">
					<CardContent className="flex items-start gap-3 p-4 text-sm">
						<AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
						<div>
							<p className="font-medium text-destructive">Verwerking gefaald</p>
							<p className="mt-1 text-xs text-muted-foreground">{doc.status_reason}</p>
						</div>
					</CardContent>
				</Card>
			)}

			<div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
				{/* Preview links — iframe voor PDF, <img> voor afbeeldingen */}
				<Card className="xl:col-span-3">
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground">Preview</CardTitle>
					</CardHeader>
					<CardContent className="p-0">
						{doc.mime_type?.startsWith('image/') ? (
							<div className="flex h-[75vh] items-center justify-center overflow-auto rounded-b-xl border-t bg-muted/20 p-4">
								<img
									src={documentPdfUrl(id)}
									alt={doc.original_filename ?? 'Afbeelding'}
									className="max-h-full max-w-full object-contain"
								/>
							</div>
						) : (
							<iframe
								src={documentPdfUrl(id)}
								title={doc.original_filename ?? 'Document'}
								className="h-[75vh] w-full rounded-b-xl border-t"
							/>
						)}
					</CardContent>
				</Card>

				{/* Metadata rechts */}
				<div className="space-y-4 xl:col-span-2">
					{editing ? (
						<EditMetadata
							doc={doc}
							onDone={() => {
								setEditing(false);
								queryClient.invalidateQueries({ queryKey: ['documents'] });
							}}
							onCancel={() => setEditing(false)}
						/>
					) : (
						<ReadMetadata doc={doc} />
					)}

					{!editing && <TagsCard documentId={doc.id} initialTags={normalizeTags(doc.tags)} />}

				{!editing && <NotesCard documentId={doc.id} initialNotes={doc.notes ?? ''} />}

					<Card>
						<CardHeader className="pb-3">
							<button
								type="button"
								onClick={() => setAuditOpen((o) => !o)}
								className="flex w-full items-center justify-between text-left"
								aria-expanded={auditOpen}
							>
								<CardTitle className="text-sm font-medium">Audit-trail ({audit.length})</CardTitle>
								{auditOpen ? (
									<ChevronDown className="h-4 w-4 text-muted-foreground" />
								) : (
									<ChevronRight className="h-4 w-4 text-muted-foreground" />
								)}
							</button>
						</CardHeader>
						{auditOpen && (
							<CardContent className="space-y-2.5">
								{audit.length === 0 && <p className="text-xs text-muted-foreground">Geen events.</p>}
								{audit.map((event) => (
									<div key={event.occurred_at + event.event_type} className="flex items-start gap-2 text-xs">
										<div className="flex w-28 shrink-0 flex-col text-muted-foreground">
											<span className="font-mono">{formatDateTimeISO(event.occurred_at)}</span>
										</div>
										<div className="flex-1">
											<div className="font-medium">
												{AUDIT_EVENT_LABELS[event.event_type] ?? event.event_type}
											</div>
											{event.error_message && (
												<p className="mt-0.5 text-destructive">{event.error_message}</p>
											)}
										</div>
									</div>
								))}
							</CardContent>
						)}
					</Card>
				</div>
			</div>
		</div>
	);
}

function ReadMetadata({ doc }: { doc: DocumentDetail }) {
	// Bedragen, vervaldatum en betaalstatus tonen we alleen wanneer de Mistral-
	// classificatie ze gevuld heeft. Geen voorgekookte whitelist op document_type:
	// een aanmaning/beschikking met bedrag wordt zo ook netjes weergegeven.
	const isInkoop = doc.target === 'exact';
	return (
		<Card>
			<CardHeader className="pb-3">
				<CardTitle className="text-sm font-medium">Metadata</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3 text-sm">
				<Field label="Type">
					<DocumentTypeLabel type={doc.document_type} />
				</Field>
				<Field label="Richting">
					{doc.direction === 'incoming' ? 'Inkomend' : doc.direction === 'outgoing' ? 'Uitgaand' : '—'}
				</Field>
				<Separator />
				{/* Voor verkoopfacturen (direction=outgoing) is counterparty de
				    GEADRESSEERDE (klant), niet de afzender. Voor inkoop/binnenkomend
				    en richting-onbekend blijft 't 'Afzender'. */}
				<Field label={doc.direction === 'outgoing' ? 'Klant' : 'Afzender'}>
					<div className="space-y-0.5">
						<div>{doc.counterparty_name ?? '—'}</div>
						{doc.counterparty_address && (
							<pre className="whitespace-pre-wrap font-sans text-xs text-muted-foreground">
								{doc.counterparty_address}
							</pre>
						)}
						{doc.counterparty_phone && (
							<div className="text-xs text-muted-foreground">tel. {doc.counterparty_phone}</div>
						)}
						{doc.counterparty_email && (
							<div className="text-xs text-muted-foreground">
								<a href={`mailto:${doc.counterparty_email}`} className="hover:underline">
									{doc.counterparty_email}
								</a>
							</div>
						)}
						{doc.counterparty_website && (
							<div className="text-xs text-muted-foreground">
								<a
									href={doc.counterparty_website.startsWith('http') ? doc.counterparty_website : `https://${doc.counterparty_website}`}
									target="_blank"
									rel="noreferrer"
									className="hover:underline"
								>
									{doc.counterparty_website}
								</a>
							</div>
						)}
						{doc.counterparty_vat && (
							<div className="text-xs text-muted-foreground">BTW {doc.counterparty_vat}</div>
						)}
						{doc.counterparty_kvk && (
							<div className="text-xs text-muted-foreground">KvK {doc.counterparty_kvk}</div>
						)}
						{doc.counterparty_iban && (
							<div className="font-mono text-xs text-muted-foreground">{doc.counterparty_iban}</div>
						)}
					</div>
				</Field>
				<Field label={doc.direction === 'outgoing' ? 'Afzender' : 'Geadresseerde'}>
					{doc.direction === 'outgoing' ? (
						<div>{doc.entity_name ?? '—'}</div>
					) : doc.addressee ? (
						<pre className="whitespace-pre-wrap font-sans text-sm">{doc.addressee}</pre>
					) : (
						<span className="text-muted-foreground">—</span>
					)}
				</Field>
				<Separator />
				<Field label="Documentdatum">{doc.document_date ? formatDateISO(doc.document_date) : '—'}</Field>
				{doc.due_date && (
					<Field label="Vervaldatum">{formatDateISO(doc.due_date)}</Field>
				)}
				<Field label="Referentie">{doc.reference ?? '—'}</Field>
				{(doc.amount_total || doc.amount_excl_vat || doc.vat_amount || doc.insurance_tax_amount) && (
					<>
						<Separator />
						{doc.amount_total && (
							<Field label="Totaal">{formatAmount(doc.amount_total, doc.currency ?? 'EUR')}</Field>
						)}
						{doc.amount_excl_vat && (
							<Field
								label={doc.insurance_tax_amount && !doc.vat_amount ? 'Excl. assurantiebelasting' : 'Excl. BTW'}
							>
								{formatAmount(doc.amount_excl_vat, doc.currency ?? 'EUR')}
							</Field>
						)}
						{doc.vat_amount && (
							<Field label="BTW">{formatAmount(doc.vat_amount, doc.currency ?? 'EUR')}</Field>
						)}
						{doc.insurance_tax_amount && (
							<Field label="Assurantiebelasting">
								{formatAmount(doc.insurance_tax_amount, doc.currency ?? 'EUR')}
							</Field>
						)}
					</>
				)}
				{isInkoop && doc.payment_status && (
					<>
						<Separator />
						<Field label="Betaalstatus">
							<span className="inline-flex items-center gap-1.5">
								<span
									className={`h-2 w-2 rounded-full ${PAYMENT_STATUS_DOT[doc.payment_status]}`}
									aria-hidden
								/>
								<span>
									{PAYMENT_STATUS_LABELS[doc.payment_status]}
									{doc.payment_status === 'paid' && doc.payment_date && ` op ${formatDateISO(doc.payment_date)}`}
								</span>
							</span>
						</Field>
					</>
				)}
				<Separator />
				<Field label="Samenvatting">
					<p className="whitespace-pre-line text-sm">{doc.summary ?? '—'}</p>
				</Field>
				<Separator />
				<Field label="Confidence">
					{doc.confidence_overall ? `${(Number.parseFloat(doc.confidence_overall) * 100).toFixed(0)}%` : '—'}
				</Field>
				<Field label="Mail van">{doc.source_email_from ?? '—'}</Field>
				<Field label="Ingekomen">{formatDateTimeISO(doc.created_at)}</Field>
			</CardContent>
		</Card>
	);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="grid grid-cols-[8rem_1fr] gap-3">
			<dt className="pt-0.5 text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
			<dd className="break-words">{children}</dd>
		</div>
	);
}

function EditMetadata({ doc, onDone, onCancel }: { doc: DocumentDetail; onDone: () => void; onCancel: () => void }) {
	const [form, setForm] = useState({
		document_type: doc.document_type ?? '',
		direction: doc.direction ?? '',
		counterparty_name: doc.counterparty_name ?? '',
		counterparty_address: doc.counterparty_address ?? '',
		counterparty_phone: doc.counterparty_phone ?? '',
		counterparty_email: doc.counterparty_email ?? '',
		counterparty_website: doc.counterparty_website ?? '',
		counterparty_vat: doc.counterparty_vat ?? '',
		counterparty_kvk: doc.counterparty_kvk ?? '',
		counterparty_iban: doc.counterparty_iban ?? '',
		document_date: doc.document_date ?? '',
		due_date: doc.due_date ?? '',
		reference: doc.reference ?? '',
		reference_type: doc.reference_type ?? '',
		amount_total: doc.amount_total ?? '',
		amount_excl_vat: doc.amount_excl_vat ?? '',
		vat_amount: doc.vat_amount ?? '',
		currency: doc.currency ?? 'EUR',
		summary: doc.summary ?? '',
		subject: doc.subject ?? '',
		needs_review: doc.needs_review ? true : false,
	});
	const [error, setError] = useState<string | null>(null);

	const mutation = useMutation({
		mutationFn: () => {
			const patch: Record<string, unknown> = {};
			const pushIfChanged = (key: keyof typeof form, currentValue: unknown) => {
				const formVal = form[key];
				const normalized = typeof formVal === 'string' ? (formVal.trim() === '' ? null : formVal) : formVal;
				if (normalized !== currentValue && !(normalized == null && currentValue == null)) {
					patch[key] = normalized;
				}
			};
			pushIfChanged('document_type', doc.document_type);
			pushIfChanged('direction', doc.direction);
			pushIfChanged('counterparty_name', doc.counterparty_name);
			pushIfChanged('counterparty_address', doc.counterparty_address);
			pushIfChanged('counterparty_phone', doc.counterparty_phone);
			pushIfChanged('counterparty_email', doc.counterparty_email);
			pushIfChanged('counterparty_website', doc.counterparty_website);
			pushIfChanged('counterparty_vat', doc.counterparty_vat);
			pushIfChanged('counterparty_kvk', doc.counterparty_kvk);
			pushIfChanged('counterparty_iban', doc.counterparty_iban);
			pushIfChanged('document_date', doc.document_date);
			pushIfChanged('due_date', doc.due_date);
			pushIfChanged('reference', doc.reference);
			pushIfChanged('reference_type', doc.reference_type);
			pushIfChanged('amount_total', doc.amount_total);
			pushIfChanged('amount_excl_vat', doc.amount_excl_vat);
			pushIfChanged('vat_amount', doc.vat_amount);
			pushIfChanged('currency', doc.currency);
			pushIfChanged('summary', doc.summary);
			pushIfChanged('subject', doc.subject);
			if (form.needs_review !== Boolean(doc.needs_review)) patch.needs_review = form.needs_review;
			// Verwijder lege required-velden uit de patch (schema zou ze anders afkeuren)
			if ('counterparty_name' in patch && !patch.counterparty_name) delete patch.counterparty_name;
			if ('summary' in patch && !patch.summary) delete patch.summary;
			return patchDocument(doc.id, patch);
		},
		onSuccess: () => {
			setError(null);
			onDone();
		},
		onError: (err: unknown) => {
			const message =
				err && typeof err === 'object' && 'body' in err
					? JSON.stringify((err as { body: unknown }).body)
					: String(err);
			setError(message);
		},
	});

	const setField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
		setForm((f) => ({ ...f, [key]: value }));

	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between pb-3">
				<CardTitle className="text-sm font-medium">Metadata bewerken</CardTitle>
				<Button size="sm" variant="ghost" onClick={onCancel}>
					<X className="h-4 w-4" />
				</Button>
			</CardHeader>
			<CardContent className="space-y-3 text-sm">
				<div className="grid grid-cols-2 gap-3">
					<div>
						<Label>Type</Label>
						<Select value={form.document_type} onChange={(e) => setField('document_type', e.target.value)}>
							<option value="">—</option>
							{DOCUMENT_TYPES.map((t) => (
								<option key={t.value} value={t.value}>
									{t.label}
								</option>
							))}
						</Select>
					</div>
					<div>
						<Label>Richting</Label>
						<Select value={form.direction} onChange={(e) => setField('direction', e.target.value)}>
							<option value="">—</option>
							{DIRECTIONS.map((d) => (
								<option key={d.value} value={d.value}>
									{d.label}
								</option>
							))}
						</Select>
					</div>
				</div>
				<div>
					<Label>{form.direction === 'outgoing' ? 'Klant' : 'Afzender'}</Label>
					<Input
						value={form.counterparty_name}
						onChange={(e) => setField('counterparty_name', e.target.value)}
					/>
				</div>
				<div>
					<Label>Adres afzender</Label>
					<Textarea
						rows={2}
						placeholder="Straat 1&#10;1234 AB Plaats"
						value={form.counterparty_address}
						onChange={(e) => setField('counterparty_address', e.target.value)}
					/>
				</div>
				<div className="grid grid-cols-2 gap-3">
					<div>
						<Label>Telefoon</Label>
						<Input
							value={form.counterparty_phone}
							onChange={(e) => setField('counterparty_phone', e.target.value)}
						/>
					</div>
					<div>
						<Label>E-mailadres</Label>
						<Input
							type="email"
							value={form.counterparty_email}
							onChange={(e) => setField('counterparty_email', e.target.value)}
						/>
					</div>
				</div>
				<div className="grid grid-cols-2 gap-3">
					<div>
						<Label>Website</Label>
						<Input
							value={form.counterparty_website}
							onChange={(e) => setField('counterparty_website', e.target.value)}
						/>
					</div>
					<div>
						<Label>BTW-nummer</Label>
						<Input
							value={form.counterparty_vat}
							onChange={(e) => setField('counterparty_vat', e.target.value)}
						/>
					</div>
				</div>
				<div className="grid grid-cols-2 gap-3">
					<div>
						<Label>KvK-nummer</Label>
						<Input
							value={form.counterparty_kvk}
							onChange={(e) => setField('counterparty_kvk', e.target.value)}
						/>
					</div>
					<div>
						<Label>IBAN</Label>
						<Input
							value={form.counterparty_iban}
							onChange={(e) => setField('counterparty_iban', e.target.value)}
						/>
					</div>
				</div>
				<div className="grid grid-cols-2 gap-3">
					<div>
						<Label>Documentdatum</Label>
						<NlDateInput
							value={form.document_date}
							onChange={(iso) => setField('document_date', iso)}
						/>
					</div>
					<div>
						<Label>Vervaldatum</Label>
						<NlDateInput value={form.due_date} onChange={(iso) => setField('due_date', iso)} />
					</div>
				</div>
				<div className="grid grid-cols-2 gap-3">
					<div>
						<Label>Referentie</Label>
						<Input value={form.reference} onChange={(e) => setField('reference', e.target.value)} />
					</div>
					<div>
						<Label>Referentie-type</Label>
						<Select value={form.reference_type} onChange={(e) => setField('reference_type', e.target.value)}>
							<option value="">—</option>
							{REFERENCE_TYPES.map((r) => (
								<option key={r.value} value={r.value}>
									{r.label}
								</option>
							))}
						</Select>
					</div>
				</div>
				<div className="grid grid-cols-3 gap-3">
					<div>
						<Label>Totaal</Label>
						<Input
							inputMode="decimal"
							value={form.amount_total}
							onChange={(e) => setField('amount_total', e.target.value)}
							placeholder="0.00"
						/>
					</div>
					<div>
						<Label>Excl. BTW</Label>
						<Input
							inputMode="decimal"
							value={form.amount_excl_vat}
							onChange={(e) => setField('amount_excl_vat', e.target.value)}
						/>
					</div>
					<div>
						<Label>BTW</Label>
						<Input inputMode="decimal" value={form.vat_amount} onChange={(e) => setField('vat_amount', e.target.value)} />
					</div>
				</div>
				<div>
					<Label>Onderwerp</Label>
					<Input value={form.subject} onChange={(e) => setField('subject', e.target.value)} />
				</div>
				<div>
					<Label>Samenvatting</Label>
					<Textarea rows={3} value={form.summary} onChange={(e) => setField('summary', e.target.value)} />
				</div>
				<div className="flex items-center gap-2">
					<input
						type="checkbox"
						id="needs_review"
						checked={form.needs_review}
						onChange={(e) => setField('needs_review', e.target.checked)}
					/>
					<label htmlFor="needs_review" className="text-xs">
						Markeer als "review nodig"
					</label>
				</div>

				{error && (
					<div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
						{error}
					</div>
				)}

				<div className="flex justify-end gap-2 pt-2">
					<Button variant="outline" size="sm" onClick={onCancel}>
						Annuleren
					</Button>
					<Button size="sm" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
						{mutation.isPending ? 'Opslaan…' : 'Opslaan'}
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}

// Tags-veld kan in D1 als JSON-array of -string staan (afhankelijk van of de
// API het al heeft geparsed). Deze helper geeft altijd een array terug.
function normalizeTags(raw: DocumentDetail['tags']): string[] {
	if (Array.isArray(raw)) return raw;
	if (typeof raw === 'string') {
		try {
			const parsed = JSON.parse(raw);
			return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
		} catch {
			return [];
		}
	}
	return [];
}

// Eigen Card voor tags. Buiten de edit-modus zichtbaar zodat tags toevoegen
// één klik kost; PATCH gaat per add/remove met de volledige nieuwe lijst.
function TagsCard({ documentId, initialTags }: { documentId: string; initialTags: string[] }) {
	const queryClient = useQueryClient();
	const [tags, setTags] = useState<string[]>(initialTags);
	const [adding, setAdding] = useState(false);
	const [input, setInput] = useState('');
	const [error, setError] = useState<string | null>(null);

	// Houd lokale state in sync als de query refetcht (bv. na save elders).
	useEffect(() => {
		setTags(initialTags);
	}, [initialTags]);

	// Suggesties uit andere documenten waarvoor de user toegang heeft.
	const suggestionsQuery = useQuery({
		queryKey: ['document-tags'],
		queryFn: () => api.documentTags(),
	});
	const suggestions = (suggestionsQuery.data?.tags ?? []).filter((t) => !tags.includes(t));

	const mutation = useMutation({
		mutationFn: (next: string[]) => patchDocument(documentId, { tags: next }),
		onMutate: (next) => {
			setError(null);
			const prev = tags;
			setTags(next);
			return { prev };
		},
		onError: (err: unknown, _next, ctx) => {
			if (ctx?.prev) setTags(ctx.prev);
			const message =
				err && typeof err === 'object' && 'body' in err
					? JSON.stringify((err as { body: unknown }).body)
					: String(err);
			setError(message);
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['documents', 'detail', documentId] }),
				queryClient.invalidateQueries({ queryKey: ['documents'] }),
				queryClient.invalidateQueries({ queryKey: ['document-tags'] }),
			]);
		},
	});

	const addTag = (raw: string) => {
		const tag = raw.trim().toLowerCase().replace(/\s+/g, '-');
		if (!tag) return;
		if (tags.includes(tag)) return;
		if (tags.length >= 20) {
			setError('Maximaal 20 tags per document.');
			return;
		}
		mutation.mutate([...tags, tag]);
		setInput('');
		setAdding(false);
	};
	const removeTag = (tag: string) => {
		mutation.mutate(tags.filter((t) => t !== tag));
	};

	return (
		<Card>
			<CardHeader className="pb-3">
				<CardTitle className="text-sm font-medium">Tags</CardTitle>
			</CardHeader>
			<CardContent className="space-y-2 text-sm">
				<div className="flex flex-wrap items-center gap-1.5">
					{tags.length === 0 && !adding && (
						<span className="text-xs text-muted-foreground">Nog geen tags.</span>
					)}
					{tags.map((tag) => (
						<Badge key={tag} variant="muted" className="gap-1 pr-1">
							{tag}
							<button
								type="button"
								onClick={() => removeTag(tag)}
								className="rounded-full p-0.5 hover:bg-muted-foreground/10"
								aria-label={`Verwijder tag ${tag}`}
								disabled={mutation.isPending}
							>
								<X className="h-3 w-3" />
							</button>
						</Badge>
					))}
					{adding ? (
						<Input
							autoFocus
							value={input}
							onChange={(e) => setInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault();
									addTag(input);
								}
								if (e.key === 'Escape') {
									e.preventDefault();
									setInput('');
									setAdding(false);
								}
							}}
							onBlur={() => {
								if (input.trim()) addTag(input);
								else setAdding(false);
							}}
							placeholder="naam-van-tag"
							className="h-7 w-40 text-xs"
							list="tag-suggestions"
						/>
					) : (
						<Button
							variant="ghost"
							size="sm"
							className="h-7 px-2 text-xs"
							onClick={() => setAdding(true)}
							disabled={mutation.isPending}
						>
							<Plus className="mr-1 h-3 w-3" />
							Tag toevoegen
						</Button>
					)}
					<datalist id="tag-suggestions">
						{suggestions.map((s) => (
							<option key={s} value={s} />
						))}
					</datalist>
				</div>
				{error && (
					<p className="text-xs text-destructive">{error}</p>
				)}
				<p className="text-[11px] text-muted-foreground">
					Tags worden lowercase en spaties worden koppeltekens. Klik later op een tag in het filter om documenten te vinden.
				</p>
			</CardContent>
		</Card>
	);
}

// Vrije-tekst opmerking. Sla expliciet op met "Opslaan"-knop; geen autosave
// zodat half-getypte gedachten niet ongewild bewaard worden.
function NotesCard({ documentId, initialNotes }: { documentId: string; initialNotes: string }) {
	const queryClient = useQueryClient();
	const [draft, setDraft] = useState(initialNotes);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setDraft(initialNotes);
	}, [initialNotes]);

	const dirty = draft !== initialNotes;

	const mutation = useMutation({
		mutationFn: () =>
			patchDocument(documentId, {
				notes: draft.trim() === '' ? null : draft,
			}),
		onSuccess: async () => {
			setError(null);
			await queryClient.invalidateQueries({ queryKey: ['documents', 'detail', documentId] });
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between pb-3">
				<CardTitle className="text-sm font-medium">Opmerking</CardTitle>
				{dirty && (
					<Button
						size="sm"
						variant="outline"
						className="h-7 px-3"
						onClick={() => mutation.mutate()}
						disabled={mutation.isPending}
					>
						{mutation.isPending ? 'Opslaan…' : 'Opslaan'}
					</Button>
				)}
			</CardHeader>
			<CardContent className="space-y-2 text-sm">
				<Textarea
					rows={4}
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					placeholder="Eigen aantekening bij dit document — niet zichtbaar voor de afzender."
					maxLength={5000}
				/>
				{error && <p className="text-xs text-destructive">{error}</p>}
				{!dirty && initialNotes === '' && (
					<p className="text-[11px] text-muted-foreground">
						Vrije tekst, alleen zichtbaar voor users met toegang tot deze entiteit.
					</p>
				)}
			</CardContent>
		</Card>
	);
}
